import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PencilIcon,
  PlayIcon,
  PauseIcon,
  StopIcon,
  TrashIcon,
  ArrowPathIcon,
  EnvelopeIcon,
  DevicePhoneMobileIcon,
  UserGroupIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  EyeIcon,
  CursorArrowRaysIcon,
  LinkIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { CampaignMetricsCharts } from '../../components/Campaigns/CampaignMetricsCharts';
import { CampaignLoadingSkeleton } from '../../components/Campaigns/CampaignLoadingSkeleton';
import { CampaignEmptyState } from '../../components/Campaigns/CampaignEmptyState';
import type { Campaign, CampaignMetrics, CampaignContact, CampaignContactsResponse } from '../../types/campaign';

export const CampaignDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [contactStatusFilter, setContactStatusFilter] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const contactsPerPage = 20;

  // Fetch campaign
  const { data: campaign, isLoading: isLoadingCampaign, error: campaignError } = useQuery<Campaign>({
    queryKey: ['campaign', id],
    queryFn: async () => {
      const response = await api.campaigns.get(id!);
      return response.data;
    },
    enabled: !!id,
    // Auto-refresh if campaign is running or scheduled (actively processing)
    refetchInterval: (data) => {
      const status = data?.status;
      return (status === 'running' || status === 'scheduled') ? 10000 : false; // Refresh every 10s if running/scheduled
    },
  });

  // Fetch campaign metrics
  const { data: metrics, isLoading: isLoadingMetrics } = useQuery<CampaignMetrics>({
    queryKey: ['campaign-metrics', id, campaign?.status], // Include status in query key to trigger refetch when status changes
    queryFn: async () => {
      const response = await api.campaigns.getMetrics(id!);
      return response.data;
    },
    enabled: !!id && !!campaign, // Wait for campaign to load
    // Auto-refresh if campaign is running or scheduled (actively processing)
    refetchInterval: () => {
      // Check campaign status from the query cache
      const campaignData = queryClient.getQueryData<Campaign>(['campaign', id]);
      const status = campaignData?.status;
      return (status === 'running' || status === 'scheduled') ? 10000 : false; // Refresh every 10s if running/scheduled
    },
  });

  // Fetch campaign contacts with pagination
  const { data: contactsData, isLoading: isLoadingContacts } = useQuery<CampaignContactsResponse>({
    queryKey: ['campaign-contacts', id, contactStatusFilter, currentPage, campaign?.status], // Include status in query key
    queryFn: async () => {
      const offset = (currentPage - 1) * contactsPerPage;
      const response = await api.campaigns.getContacts(
        id!, 
        contactStatusFilter || undefined,
        contactsPerPage,
        offset
      );
      return response.data;
    },
    enabled: !!id && !!campaign, // Wait for campaign to load
    // Auto-refresh contacts if campaign is running or scheduled
    refetchInterval: () => {
      // Check campaign status from the query cache
      const campaignData = queryClient.getQueryData<Campaign>(['campaign', id]);
      const status = campaignData?.status;
      return (status === 'running' || status === 'scheduled') ? 15000 : false; // Refresh every 15s if running/scheduled
    },
  });

  // Extract contacts and pagination data
  const contacts = contactsData?.contacts || [];
  const totalContacts = contactsData?.total || 0;

  // Check if campaign is webhook-only
  const isWebhookOnly = campaign?.filters?.contact_ids && 
    Array.isArray(campaign.filters.contact_ids) && 
    campaign.filters.contact_ids.length === 0;

  // Fetch webhooks connected to this campaign
  const { data: campaignWebhooks } = useQuery({
    queryKey: ['campaign-webhooks', id],
    queryFn: async () => {
      const response = await api.campaigns.getWebhooks(id!);
      return response.data;
    },
    enabled: !!id,
  });

  // Fetch webhook events for this campaign
  const { data: webhookEvents, error: webhookEventsError } = useQuery({
    queryKey: ['campaign-webhook-events', id],
    queryFn: async () => {
      const response = await api.campaigns.getWebhookEvents(id!, 20);
      return response.data;
    },
    enabled: !!id && !!campaignWebhooks && campaignWebhooks.length > 0,
    retry: 1, // Only retry once instead of default 3 times
    retryDelay: 2000, // Wait 2 seconds before retry
    refetchInterval: () => {
      const campaignData = queryClient.getQueryData<Campaign>(['campaign', id]);
      const status = campaignData?.status;
      return (status === 'running' || status === 'scheduled') ? 15000 : false;
    },
  });

  // Handle webhook events errors gracefully (non-critical data)
  useEffect(() => {
    if (webhookEventsError) {
      // Only log, don't show toast for this non-critical data
      console.warn('Failed to load webhook events:', webhookEventsError);
    }
  }, [webhookEventsError]);

  // Retry failed enqueue mutation
  const retryEnqueueMutation = useMutation({
    mutationFn: async (eventId: string) => {
      const response = await api.automation.retryFailedEnqueue(eventId);
      return response.data;
    },
    onSuccess: (data) => {
      if (data.success) {
        toast.success('Campaign enqueue retried successfully');
        queryClient.invalidateQueries({ queryKey: ['campaign-webhook-events', id] });
        queryClient.invalidateQueries({ queryKey: ['campaign-contacts', id] });
        queryClient.invalidateQueries({ queryKey: ['campaign', id] });
      } else {
        toast.error(`Retry failed: ${data.errors?.join(', ') || 'Unknown error'}`);
      }
    },
    onError: (error: any) => {
      toast.error(`Failed to retry: ${error.response?.data?.detail || error.message}`);
    },
  });

  // Mutations
  const deleteMutation = useMutation({
    mutationFn: () => api.campaigns.delete(id!),
    onSuccess: () => {
      toast.success('Campaign deleted successfully');
      navigate('/campaigns');
    },
    onError: (error: any) => {
      toast.error(`Failed to delete campaign: ${error.response?.data?.detail || error.message}`);
    },
  });

  const enqueueMutation = useMutation({
    mutationFn: () => api.campaigns.enqueue(id!, { dry_run: false }),
    onMutate: async () => {
      // Optimistically update campaign status to 'scheduled' immediately
      await queryClient.cancelQueries({ queryKey: ['campaign', id] });
      
      const previousCampaign = queryClient.getQueryData<Campaign>(['campaign', id]);
      
      if (previousCampaign) {
        queryClient.setQueryData<Campaign>(['campaign', id], {
          ...previousCampaign,
          status: 'scheduled',
        });
      }
      
      return { previousCampaign };
    },
    onSuccess: async (response: any) => {
      // Show detailed results
      const data = response.data;
      if (data.queued_contacts && data.queued_contacts > 0) {
        toast.success(`Campaign started - ${data.queued_contacts} contact(s) queued`);
      } else {
        // Check if this is a webhook-only campaign
        const campaignData = queryClient.getQueryData<Campaign>(['campaign', id]);
        const isWebhookOnlyCampaign = campaignData?.filters?.contact_ids && 
          Array.isArray(campaignData.filters.contact_ids) && 
          campaignData.filters.contact_ids.length === 0;
        
        if (isWebhookOnlyCampaign) {
          toast.info(
            `Campaign started. This is a webhook-only campaign - it will send to contacts added via automation/webhooks. No existing contacts were queued.`,
            { duration: 6000 }
          );
        } else {
          toast.warning(`Campaign started but no contacts were queued`);
        }
      }
      
      // Show warnings about skipped contacts
      if (data.skipped_contacts && data.skipped_contacts > 0) {
        toast.info(`${data.skipped_contacts} contact(s) skipped`);
      }
      
      // Show errors if any
      if (data.errors && data.errors.length > 0) {
        data.errors.forEach((error: string) => {
          toast.error(error);
        });
      }
      
      // Immediately refetch campaign data to get updated status and metrics
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['campaign', id] }),
        queryClient.refetchQueries({ queryKey: ['campaign-metrics', id] }),
        queryClient.refetchQueries({ queryKey: ['campaign-contacts', id] }),
        queryClient.refetchQueries({ queryKey: ['campaigns'] }), // Also update list if user navigates back
      ]);
    },
    onError: (error: any, variables, context) => {
      // Rollback optimistic update on error
      if (context?.previousCampaign) {
        queryClient.setQueryData<Campaign>(['campaign', id], context.previousCampaign);
      }
      toast.error(`Failed to start campaign: ${error.response?.data?.detail || error.message}`);
    },
  });

  const pauseMutation = useMutation({
    mutationFn: () => api.campaigns.pause(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign', id] });
      toast.success('Campaign paused successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to pause campaign: ${error.response?.data?.detail || error.message}`);
    },
  });

  const resumeMutation = useMutation({
    mutationFn: () => api.campaigns.resume(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign', id] });
      toast.success('Campaign resumed successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to resume campaign: ${error.response?.data?.detail || error.message}`);
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => api.campaigns.cancel(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign', id] });
      toast.success('Campaign cancelled successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to cancel campaign: ${error.response?.data?.detail || error.message}`);
    },
  });

  // Status badge
  const StatusBadge: React.FC<{ status: Campaign['status'] }> = ({ status }) => {
    // Check if this is an automation campaign waiting for webhooks
    const isAutomationWaiting = isWebhookOnly && 
      campaign.status === 'draft' && 
      campaignWebhooks && 
      campaignWebhooks.length > 0 &&
      (campaign.total_contacts === 0 || !campaign.total_contacts);
    
    const statusStyles = {
      draft: isAutomationWaiting ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-800',
      scheduled: 'bg-blue-100 text-blue-800',
      running: 'bg-green-100 text-green-800 animate-pulse',
      paused: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    };

    const statusText = isAutomationWaiting 
      ? 'Waiting for Automation' 
      : status.charAt(0).toUpperCase() + status.slice(1);

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusStyles[status]}`}>
        {isAutomationWaiting && <CursorArrowRaysIcon className="h-4 w-4 mr-1" />}
        {statusText}
      </span>
    );
  };

  // Channel badge
  const ChannelBadge: React.FC<{ channel: Campaign['channel'] }> = ({ channel }) => {
    const channelConfig = {
      email: { icon: EnvelopeIcon, color: 'bg-purple-100 text-purple-800' },
      sms: { icon: DevicePhoneMobileIcon, color: 'bg-indigo-100 text-indigo-800' },
      both: { icon: UserGroupIcon, color: 'bg-pink-100 text-pink-800' },
    };

    const config = channelConfig[channel];
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        <Icon className="h-4 w-4 mr-1" />
        {channel.toUpperCase()}
      </span>
    );
  };

  // Format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Format percentage
  const formatPercentage = (value: number) => {
    return `${Math.round(value * 100)}%`;
  };

  // Calculate progress
  const calculateProgress = () => {
    if (!campaign || !campaign.total_contacts || campaign.total_contacts === 0) return 0;
    const processed = (campaign.sent_count || 0);
    return Math.round((processed / campaign.total_contacts) * 100);
  };

  // Calculate pagination
  const totalPages = Math.ceil(totalContacts / contactsPerPage);

  // Loading state
  if (isLoadingCampaign) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <CampaignLoadingSkeleton type="detail" />
      </div>
    );
  }

  // Error state
  if (campaignError || !campaign) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="rounded-md bg-red-50 p-4">
          <h3 className="text-sm font-medium text-red-800">Error loading campaign</h3>
          <p className="mt-2 text-sm text-red-700">
            {(campaignError as any)?.message || 'Campaign not found'}
          </p>
          <button
            onClick={() => navigate('/campaigns')}
            className="mt-4 text-sm text-red-600 hover:text-red-500"
          >
            ← Back to Campaigns
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/campaigns')}
          className="mb-4 inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Campaigns
        </button>

        <div className="sm:flex sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">{campaign.name}</h1>
            {campaign.description && (
              <p className="mt-1 text-sm text-gray-500">{campaign.description}</p>
            )}
            <div className="mt-2 flex items-center space-x-3 flex-wrap gap-2">
              <StatusBadge status={campaign.status} />
              <ChannelBadge channel={campaign.channel} />
              {isWebhookOnly && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  <CursorArrowRaysIcon className="h-4 w-4 mr-1" />
                  Webhook-Only
                </span>
              )}
              {campaignWebhooks && campaignWebhooks.length > 0 && (
                <Link
                  to={`/settings?tab=automations#webhook-${campaignWebhooks[0].id}`}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800 hover:bg-indigo-200 transition-colors"
                >
                  <LinkIcon className="h-4 w-4 mr-1" />
                  Connected to Automation
                </Link>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-4 sm:mt-0 flex items-center space-x-2">
            {campaign.status === 'draft' && (
              <>
                <button
                  onClick={() => navigate(`/campaigns/${id}/edit`)}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <PencilIcon className="h-4 w-4 mr-1" />
                  Edit
                </button>
                <button
                  onClick={() => enqueueMutation.mutate()}
                  disabled={enqueueMutation.isPending}
                  className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                >
                  <PlayIcon className="h-4 w-4 mr-1" />
                  Start
                </button>
              </>
            )}

            {campaign.status === 'running' && (
              <>
                <button
                  onClick={() => pauseMutation.mutate()}
                  disabled={pauseMutation.isPending}
                  className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-yellow-600 hover:bg-yellow-700 disabled:opacity-50"
                >
                  <PauseIcon className="h-4 w-4 mr-1" />
                  Pause
                </button>
                <button
                  onClick={() => cancelMutation.mutate()}
                  disabled={cancelMutation.isPending}
                  className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                >
                  <StopIcon className="h-4 w-4 mr-1" />
                  Cancel
                </button>
              </>
            )}

            {campaign.status === 'paused' && (
              <>
                <button
                  onClick={() => resumeMutation.mutate()}
                  disabled={resumeMutation.isPending}
                  className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                >
                  <ArrowPathIcon className="h-4 w-4 mr-1" />
                  Resume
                </button>
                <button
                  onClick={() => cancelMutation.mutate()}
                  disabled={cancelMutation.isPending}
                  className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                >
                  <StopIcon className="h-4 w-4 mr-1" />
                  Cancel
                </button>
              </>
            )}

            {(campaign.status === 'draft' || campaign.status === 'completed' || campaign.status === 'cancelled') && (
              <button
                onClick={() => {
                  if (confirm(`Are you sure you want to delete "${campaign.name}"?`)) {
                    deleteMutation.mutate();
                  }
                }}
                disabled={deleteMutation.isPending}
                className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
              >
                <TrashIcon className="h-4 w-4 mr-1" />
                Delete
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      {(campaign.status === 'running' || campaign.status === 'paused') && (
        <div className="mb-6 bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Campaign Progress</span>
            <span className="text-sm text-gray-500">
              {campaign.sent_count || 0} of {campaign.total_contacts || 0} contacts processed
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className="bg-indigo-600 h-2.5 rounded-full transition-all duration-500"
              style={{ width: `${calculateProgress()}%` }}
            ></div>
          </div>
          <div className="mt-1 text-right text-xs text-gray-500">{calculateProgress()}%</div>
        </div>
      )}

      {/* Metrics Overview Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-6">
        {/* Total Contacts */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UserGroupIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Contacts</dt>
                  <dd className="text-lg font-semibold text-gray-900">{campaign.total_contacts || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Sent */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <PaperAirplaneIcon className="h-6 w-6 text-blue-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Sent</dt>
                  <dd className="flex items-baseline">
                    <div className="text-lg font-semibold text-gray-900">{campaign.sent_count || 0}</div>
                    <div className="ml-2 text-sm text-gray-500">
                      {metrics && formatPercentage(campaign.sent_count || 0 / (campaign.total_contacts || 1))}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Delivered */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-6 w-6 text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Delivered</dt>
                  <dd className="flex items-baseline">
                    <div className="text-lg font-semibold text-gray-900">{campaign.delivered_count || 0}</div>
                    <div className="ml-2 text-sm text-gray-500">
                      {metrics && formatPercentage(metrics.delivery_rate)}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Opened */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <EyeIcon className="h-6 w-6 text-purple-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Opened</dt>
                  <dd className="flex items-baseline">
                    <div className="text-lg font-semibold text-gray-900">{campaign.opened_count || 0}</div>
                    <div className="ml-2 text-sm text-gray-500">
                      {metrics && formatPercentage(metrics.open_rate)}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Metrics Charts */}
      {metrics && (
        <div className="mb-6">
          <CampaignMetricsCharts metrics={metrics} />
        </div>
      )}

      {/* Webhook Activity Section */}
      {campaignWebhooks && campaignWebhooks.length > 0 && webhookEvents && webhookEvents.length > 0 && (
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Webhook Activity</h3>
            <Link
              to={`/settings?tab=automations#webhook-${campaignWebhooks[0].id}`}
              className="text-sm text-indigo-600 hover:text-indigo-500 flex items-center"
            >
              View Webhook Settings
              <LinkIcon className="h-4 w-4 ml-1" />
            </Link>
          </div>
          <div className="space-y-3">
            {webhookEvents.slice(0, 10).map((event: any) => (
              <div key={event.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      event.status === 'success' ? 'bg-green-100 text-green-800' :
                      event.status === 'error' ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {event.status}
                    </span>
                    <span className="text-sm text-gray-600">{event.event_type}</span>
                  </div>
                  {event.error_message && (
                    <div className="mt-1">
                      <p className="text-xs text-red-600">{event.error_message}</p>
                      {event.status === 'error' && event.campaign_id && (
                        <button
                          onClick={() => retryEnqueueMutation.mutate(event.id)}
                          disabled={retryEnqueueMutation.isPending}
                          className="mt-1 text-xs text-indigo-600 hover:text-indigo-500 disabled:opacity-50"
                        >
                          {retryEnqueueMutation.isPending ? 'Retrying...' : 'Retry Enqueue'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div className="text-xs text-gray-500">
                  {formatDate(event.created_at)}
                </div>
              </div>
            ))}
            {webhookEvents.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">No webhook events yet</p>
            )}
          </div>
        </div>
      )}

      {/* Campaign Configuration */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Campaign Configuration</h3>
        {isWebhookOnly && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <div className="flex items-start">
              <InformationCircleIcon className="h-5 w-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm text-blue-800">
                  <strong>Webhook-Only Campaign:</strong> This campaign will only send to contacts added via automation/webhooks. 
                  No existing contacts will be included when you start the campaign.
                </p>
              </div>
            </div>
          </div>
        )}
        <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
          <div>
            <dt className="text-sm font-medium text-gray-500">Created</dt>
            <dd className="mt-1 text-sm text-gray-900">{formatDate(campaign.created_at)}</dd>
          </div>
          {campaign.scheduled_at && (
            <div>
              <dt className="text-sm font-medium text-gray-500">Scheduled</dt>
              <dd className="mt-1 text-sm text-gray-900">{formatDate(campaign.scheduled_at)}</dd>
            </div>
          )}
          {campaign.started_at && (
            <div>
              <dt className="text-sm font-medium text-gray-500">Started</dt>
              <dd className="mt-1 text-sm text-gray-900">{formatDate(campaign.started_at)}</dd>
            </div>
          )}
          {campaign.completed_at && (
            <div>
              <dt className="text-sm font-medium text-gray-500">Completed</dt>
              <dd className="mt-1 text-sm text-gray-900">{formatDate(campaign.completed_at)}</dd>
            </div>
          )}
          <div>
            <dt className="text-sm font-medium text-gray-500">Respect Consent</dt>
            <dd className="mt-1 text-sm text-gray-900">{campaign.respect_consent ? 'Yes' : 'No'}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Respect Suppression</dt>
            <dd className="mt-1 text-sm text-gray-900">{campaign.respect_suppression ? 'Yes' : 'No'}</dd>
          </div>
          {campaign.tags && campaign.tags.length > 0 && (
            <div className="sm:col-span-2">
              <dt className="text-sm font-medium text-gray-500">Tags</dt>
              <dd className="mt-1 flex flex-wrap gap-2">
                {campaign.tags.map((tag) => (
                  <span key={tag} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {tag}
                  </span>
                ))}
              </dd>
            </div>
          )}
        </dl>
      </div>

      {/* Campaign Contacts */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="sm:flex sm:items-center sm:justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Campaign Contacts</h3>
          <div className="mt-3 sm:mt-0">
            <select
              value={contactStatusFilter}
              onChange={(e) => {
                setContactStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="sent">Sent</option>
              <option value="delivered">Delivered</option>
              <option value="opened">Opened</option>
              <option value="clicked">Clicked</option>
              <option value="bounced">Bounced</option>
              <option value="unsubscribed">Unsubscribed</option>
            </select>
          </div>
        </div>

        {isLoadingContacts ? (
          <div className="py-4">
            <CampaignLoadingSkeleton type="table" rows={5} />
          </div>
        ) : contacts.length === 0 && totalContacts === 0 ? (
          <CampaignEmptyState type="no-contacts" />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">Contact</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Source</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Sent At</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Delivered At</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Opened At</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Clicked At</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {contacts.map((contact) => {
                    const source = contact.metadata?.source === 'webhook' || contact.metadata?.added_via_automation ? 'Webhook' : 'Manual';
                    return (
                      <tr key={contact.id}>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm">
                          <div className="font-medium text-gray-900">
                            {contact.contact?.first_name} {contact.contact?.last_name}
                          </div>
                          <div className="text-gray-500">{contact.contact?.email || contact.contact?.phone}</div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            source === 'Webhook' 
                              ? 'bg-indigo-100 text-indigo-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {source === 'Webhook' && <CursorArrowRaysIcon className="h-3 w-3 mr-1" />}
                            {source}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            {contact.status}
                          </span>
                        </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {contact.sent_at ? formatDate(contact.sent_at) : '-'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {contact.delivered_at ? formatDate(contact.delivered_at) : '-'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {contact.opened_at ? formatDate(contact.opened_at) : '-'}
                      </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {contact.clicked_at ? formatDate(contact.clicked_at) : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4">
                <div className="flex flex-1 justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing <span className="font-medium">{(currentPage - 1) * contactsPerPage + 1}</span> to{' '}
                      <span className="font-medium">{Math.min(currentPage * contactsPerPage, totalContacts)}</span> of{' '}
                      <span className="font-medium">{totalContacts}</span> contacts
                    </p>
                  </div>
                  <div>
                    <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                      <button
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Previous
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                            page === currentPage
                              ? 'z-10 bg-indigo-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
                              : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                      <button
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

